import React from 'react';
import Tag from 'Components/DesignSystem/Tag/Tag';
import { CLEAR_ALL } from '../constants/CategoryRelevancyConstants';

type AppliedFiltersProps = {
    filters: FilterOptions;
    onRemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    onFilterChange?: (newFilters: FilterOptions) => void;
    activeTab?: TabType;
}

const AppliedFilters = ({ filters, onRemoveFilter, onFilterChange, activeTab = 'all' }: AppliedFiltersProps) => {
    const hasFilters = Object.entries(filters).some(([key, value]) =>
        key !== 'is_curated'
            ? Array.isArray(value) && value.length > 0
            : value !== undefined || (Array.isArray(filters.curatedSelections) && filters.curatedSelections.length > 0)
    );

    if (!hasFilters) {
        return null;
    }

    const hasBothCuratedSelections =
        Array.isArray(filters.curatedSelections) &&
        filters.curatedSelections.includes('active') &&
        filters.curatedSelections.includes('inactive');

    const handleRemoveCuratedSelection = (selection: 'active' | 'inactive') => {
        if (!onFilterChange) {
            onRemoveFilter('is_curated');
            return;
        }

        const newFilters = { ...filters };

        const keepSelection = selection === 'active' ? 'inactive' : 'active';
        newFilters.curatedSelections = [keepSelection];

        newFilters.is_curated = keepSelection === 'active';

        onFilterChange(newFilters);
    };

    const shouldShowCuratedFilters = activeTab === 'all';

    /* The chips are DS `Tag outlined tone="info"` — the pinned filter-chip grammar
       (design-system §§ 203/216, same as the Performance header chips). Each Tag's
       remove button derives its testid as `<chip testid>-remove`. */
    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-4" data-testid="applied-filters">
            {filters?.categories?.map(category => (
                <Tag
                    outlined
                    tone="info"
                    key={`category-${category.category_id}`}
                    data-testid={`filter-category-${category.category_id}`}
                    label={`Category: ${category.category_name}`}
                    onRemove={() => onRemoveFilter('categories', category.category_id)}
                />
            ))}

            {filters?.category_types?.map(categoryType => (
                <Tag
                    outlined
                    tone="info"
                    key={`category-type-${categoryType.category_type_id}`}
                    data-testid={`filter-category-type-${categoryType.category_type_id}`}
                    label={`Category Type: ${categoryType.category_type_name}`}
                    onRemove={() => onRemoveFilter('category_types', categoryType.category_type_id)}
                />
            ))}

            {filters?.co_sold_categories?.map(category => (
                <Tag
                    outlined
                    tone="info"
                    key={`co-sold-category-${category.category_id}`}
                    data-testid={`filter-co-sold-category-${category.category_id}`}
                    label={`Co-sold Category: ${category.category_name}`}
                    onRemove={() => onRemoveFilter('co_sold_categories', category.category_id)}
                />
            ))}

            {filters?.co_sold_category_types?.map(categoryType => (
                <Tag
                    outlined
                    tone="info"
                    key={`co-sold-category-type-${categoryType.category_type_id}`}
                    data-testid={`filter-co-sold-category-type-${categoryType.category_type_id}`}
                    label={`Co-sold Category Type: ${categoryType.category_type_name}`}
                    onRemove={() => onRemoveFilter('co_sold_category_types', categoryType.category_type_id)}
                />
            ))}

            {shouldShowCuratedFilters && hasBothCuratedSelections ? (
                <>
                    <Tag
                        outlined
                        tone="info"
                        data-testid="filter-curated-active"
                        label="Curated: Active"
                        removeLabel="Remove Curated: Active filter"
                        onRemove={() => handleRemoveCuratedSelection('active')}
                    />
                    <Tag
                        outlined
                        tone="info"
                        data-testid="filter-curated-inactive"
                        label="Curated: Inactive"
                        removeLabel="Remove Curated: Inactive filter"
                        onRemove={() => handleRemoveCuratedSelection('inactive')}
                    />
                </>
            ) : (
                shouldShowCuratedFilters && filters?.is_curated !== undefined && (
                    <Tag
                        outlined
                        tone="info"
                        data-testid={`filter-curated-${filters.is_curated ? 'active' : 'inactive'}`}
                        label={`Curated: ${filters.is_curated ? 'Active' : 'Inactive'}`}
                        removeLabel={`Remove Curated: ${filters.is_curated ? 'Active' : 'Inactive'} filter`}
                        onRemove={() => onRemoveFilter('is_curated')}
                    />
                )
            )}

            {hasFilters && (
                <button
                    className="tw-text-accent hover:tw-underline"
                    onClick={() => onRemoveFilter('all')}
                    data-testid="clear-all-filters"
                >
                    {CLEAR_ALL}
                </button>
            )}
        </div>
    );
};

export default AppliedFilters;
